/**
 * ProjectContext - Backward-Compatible Facade
 *
 * This file now serves as a facade/adapter for the Zustand projectsStore.
 * All consumers continue to work without changes while using Zustand under the hood.
 *
 * MIGRATION STATUS: Phase 4 - Facade layer (backward compatible)
 * - All exports use Zustand projectsStore
 * - ProjectsProvider is a passthrough (no provider needed with Zustand)
 * - 7 consumers continue working without changes
 *
 * Original implementation: React Context with TTL caching (182 lines)
 * New implementation: Thin facade wrapping Zustand (60 lines - 67% reduction)
 */

import { useMemo, useCallback } from 'react';
import {
  useProjectsStore,
  useProjects as useProjectsFromStore,
  useProjectsLoading,
  useProjectsError,
  useUniqueProjects,
  useProjectsActions
} from '../../../store/projects/projectsStore';
import { useUserData } from '../../../features/auth/hooks/useUser';

/**
 * ProjectsProvider - Backward-compatible wrapper
 *
 * This is now a passthrough component. The Zustand projectsStore handles:
 * - State management (projects, loading, error)
 * - TTL-based caching (60-second cache)
 * - Automatic loading when userData becomes available
 *
 * All side effects have been moved to the store.
 */
export const ProjectsProvider = ({ children }) => {
  // No provider needed - Zustand store is global
  // Just render children directly
  return children;
};

/**
 * useProjects - Backward-compatible hook
 *
 * Returns the same API as the previous React Context implementation.
 * Consumers can continue using this without any changes.
 */
export const useProjects = () => {
  // Subscribe to Zustand store
  const projects = useProjectsFromStore();
  const uniqueProjects = useUniqueProjects();
  const loading = useProjectsLoading();
  const error = useProjectsError();
  const {
    loadProjects: storeLoadProjects,
    refreshProjects: storeRefreshProjects,
    clearCache,
    getProjectDisplayName,
    addProject,
    removeProject,
    updateProjectInStore
  } = useProjectsActions();

  // Get userData to auto-inject into store actions (matches old API where
  // consumers called loadProjects() / refreshProjects() with no arguments)
  const { userData } = useUserData();

  const loadProjects = useCallback((force = false) => {
    return storeLoadProjects(userData, force);
  }, [storeLoadProjects, userData]);

  const refreshProjects = useCallback(() => {
    return storeRefreshProjects(userData);
  }, [storeRefreshProjects, userData]);

  // Return the same API as before
  return useMemo(() => ({
    // Data
    projects,
    uniqueProjects,
    loading,
    error,

    // Functions (renamed clearCache to clearProjectsCache for backward compatibility)
    loadProjects,
    refreshProjects,
    clearProjectsCache: clearCache,
    getProjectDisplayName,
    addProject,
    removeProject,
    updateProjectInStore
  }), [
    projects,
    uniqueProjects,
    loading,
    error,
    loadProjects,
    refreshProjects,
    clearCache,
    getProjectDisplayName,
    addProject,
    removeProject,
    updateProjectInStore
  ]);
};

/**
 * Default export for backward compatibility
 */
const ProjectsContext = null; // No longer needed - Zustand doesn't use Context
export default ProjectsContext;

/**
 * Direct Zustand exports for new code
 *
 * New code can import these directly for better performance
 * (granular subscriptions - only re-render when specific fields change)
 */
export {
  useUniqueProjects,
  useProjectsLoading,
  useProjectsError,
  useProjectsActions
} from '../../../store/projects/projectsStore';
